import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Reveal, Eyebrow, Marquee, BrowserFrame, BracketLink, Marker } from "@/components/atoms";
import { LogoMark } from "@/components/Logo";
import EstimateTable from "@/components/EstimateTable";
import { IMAGES, ISSUES, QANDA, PORTAL_NAME } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 ${className}`}>{children}</section>
);

const Hero = () => (
  <div className="relative min-h-[92vh] flex items-center overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.foundry} alt="" aria-hidden="true" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-navy/85" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(10,18,48,0.25), rgba(10,18,48,0) 35%, rgba(241,241,236,1) 100%)" }} />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24 w-full">
      <Reveal><Eyebrow className="!text-paper/70">WEBSITE REDESIGN PROPOSAL</Eyebrow></Reveal>
      <Reveal delay={0.05}>
        <h1 className="font-body font-medium tracking-[-0.035em] leading-[1.05] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl mt-8 text-paper max-w-4xl" data-testid="hero-headline">
          A modern public website for Backer Marathon.
        </h1>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-paper/75 text-lg mt-8 max-w-2xl leading-relaxed">
          This proposal covers the redesign of the current site, a public product catalog with instant RFQ replacing the
          internal IMDB database, HubSpot integration, and a CMS the Marathon team controls directly.
        </p>
      </Reveal>
      <Reveal delay={0.15} className="flex flex-wrap gap-4 mt-10">
        <BracketLink to="/demo" primary light testid="hero-cta-portal-demo">Open the prototype</BracketLink>
        <BracketLink href="#estimate" light testid="hero-cta-estimate">View the estimate</BracketLink>
      </Reveal>
    </div>
  </div>
);

const StatsStrip = () => (
  <Section className="!py-0">
    <div className="relative grid grid-cols-2 md:grid-cols-4 border border-linec bg-white">
      <Marker className="-bottom-1 left-1/4 hidden md:block" />
      <Marker className="-bottom-1 left-2/4 hidden md:block" />
      <Marker className="-bottom-1 left-3/4 hidden md:block" />
      {[["10 YEARS", "Legacy IMDB server in service"], ["0", "Public search pages today"], ["1600°F", "Catalog spec ceiling"], ["1", "Owned database, at the end"]].map(([v, l]) => (
        <div key={l} className="p-6 lg:p-8 border-r border-b md:border-b-0 border-linec last:border-r-0">
          <p className="font-body font-semibold text-2xl lg:text-4xl text-ember">{v}</p>
          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-dim mt-2">{l}</p>
        </div>
      ))}
    </div>
  </Section>
);

const Issues = () => (
  <Section id="issues">
    <Reveal><Eyebrow>01 — KEY ISSUES IDENTIFIED</Eyebrow></Reveal>
    <Reveal delay={0.05}>
      <h2 className="font-body font-medium tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl mt-6 text-navy">
        Current limitations.
      </h2>
    </Reveal>
    <div className="mt-12 divide-y divide-linec border-y border-linec">
      {ISSUES.map((h, i) => (
        <Reveal key={h.n} delay={i * 0.05} className="grid md:grid-cols-[5rem_1fr_1.4fr] gap-4 py-8 items-start" data-testid={`issue-item-${h.n}`}>
          <span className="font-mono text-sm text-ember">{h.n}</span>
          <h3 className="font-body font-semibold text-xl text-navy">{h.title}</h3>
          <p className="text-mutedx leading-relaxed">{h.body}</p>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Answers = () => (
  <Section id="answers" className="!pt-0">
    <Reveal><Eyebrow>02 — ANSWERS FOR THE MARATHON TEAM</Eyebrow></Reveal>
    <Reveal delay={0.05}>
      <h2 className="font-body font-medium tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl mt-6 text-navy">
        Your questions, answered.
      </h2>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-12">
      {QANDA.map((q, i) => (
        <Reveal key={q.n} delay={i * 0.08} className="relative border border-linec bg-white ember-hover flex flex-col" data-testid={`answer-card-${q.n}`}>
          <Marker className="-top-1 -right-1" />
          <div className="p-8 flex flex-col flex-1">
            <p className="font-mono text-xs text-ember">{q.n}</p>
            <h3 className="font-body font-semibold text-lg mt-4 text-navy leading-snug">{q.q}</h3>
            <p className="text-mutedx mt-3 leading-relaxed text-sm flex-1">{q.a}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Prototype = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/rfq/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, []);
  return (
    <Section id="prototype" className="!pt-0">
      <Reveal><Eyebrow>03 — WORKING PROTOTYPE</Eyebrow></Reveal>
      <Reveal delay={0.05}>
        <h2 className="font-body font-medium tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl mt-6 text-navy">
          A functional preview of the proposed portal.
        </h2>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-mutedx text-lg mt-6 max-w-2xl leading-relaxed">
          The {PORTAL_NAME} demo is included with this proposal: search the sample catalog, filter by specification,
          add items and submit an RFQ. Submissions are stored and returned with a reference number. The calculators
          are wired into the same flow — run a watt-density check, see matched heaters from the catalog, and quote
          them in one click.
        </p>
      </Reveal>
      <Reveal delay={0.15} className="mt-10">
        <BrowserFrame url={`backermarathon.com/${PORTAL_NAME.toLowerCase()}-demo`}>
          <div className="relative">
            <img src={IMAGES.cad} alt="Product search and RFQ portal preview" className="w-full h-[280px] lg:h-[420px] object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/30 to-transparent" />
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-5 text-center px-6">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-paper/70">WORKING PROTOTYPE</p>
              <p className="font-body font-medium text-2xl lg:text-4xl max-w-xl text-white">
                {stats ? `${stats.total_products} sample SKUs loaded · ${stats.total_rfqs} RFQs submitted in-demo` : "Search. Filter. Quote in 60 seconds."}
              </p>
              <BracketLink to="/demo" primary light testid="demo-section-cta">Open the prototype &rarr;</BracketLink>
            </div>
          </div>
        </BrowserFrame>
      </Reveal>
    </Section>
  );
};

const Mockups = () => (
  <Section id="mockups" className="!pt-0">
    <Reveal><Eyebrow>04 — DESIGN MOCKUPS</Eyebrow></Reveal>
    <Reveal delay={0.05}>
      <h2 className="font-body font-medium tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl mt-6 text-navy">
        Two pages, fully designed.
      </h2>
    </Reveal>
    <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 mt-12">
      {[
        { to: "/mockup/home", img: IMAGES.foundry, tag: "MOCKUP 1 / 2", title: "Homepage", desc: "Industrial-modern hero, category grid, product spotlight — every surface pushes engineers toward a quote." },
        { to: "/mockup/product", img: IMAGES.oven, tag: "MOCKUP 2 / 2", title: "Product Line Page", desc: "Spec-driven catalog pages with clear ratings, watt density and a persistent quote path. Built for how buyers actually work." },
      ].map((m, i) => (
        <Reveal key={m.to} delay={i * 0.08}>
          <BrowserFrame url={`backermarathon.com${m.to.replace("/mockup", "").replace("home", "") || "/"}`}>
            <div className="relative">
              <img src={m.img} alt={`${m.title} redesign mockup`} className="w-full h-[260px] object-cover opacity-60" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 to-transparent" />
              <div className="absolute bottom-0 p-6">
                <p className="font-mono text-[10px] tracking-[0.3em] text-paper/70 uppercase">{m.tag}</p>
                <h3 className="font-body font-semibold text-2xl mt-2 text-white">{m.title}</h3>
                <p className="text-sm text-paper/70 mt-2">{m.desc}</p>
                <BracketLink to={m.to} light testid={`mockup-open-${m.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="mt-4 text-sm">
                  Open full mockup &rarr;
                </BracketLink>
              </div>
            </div>
          </BrowserFrame>
        </Reveal>
      ))}
    </div>
  </Section>
);

const Footer = () => (
  <footer className="bg-navy text-paper mt-10">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10">
        <div className="flex items-center gap-3">
          <LogoMark size={44} />
          <div>
            <p className="font-body font-semibold text-sm text-paper">Backer Marathon — Website Redesign Proposal</p>
            <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-paper/50 mt-1">Valid 60 days</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-3 font-body text-sm">
          <a href="#issues" className="text-paper/70 hover:text-ember transition-colors" data-testid="footer-link-issues">Key Issues</a>
          <Link to="/demo" className="text-paper/70 hover:text-ember transition-colors" data-testid="footer-link-prototype">Prototype</Link>
          <Link to="/mockup/home" className="text-paper/70 hover:text-ember transition-colors" data-testid="footer-link-mockup-1">Mockup 1</Link>
          <Link to="/mockup/product" className="text-paper/70 hover:text-ember transition-colors" data-testid="footer-link-mockup-2">Mockup 2</Link>
          <Link to="/letter" className="text-paper/70 hover:text-ember transition-colors" data-testid="footer-print-link">Print / PDF</Link>
        </div>
      </div>
    </div>
  </footer>
);

export default function Proposal() {
  return (
    <main className="grain bg-bone min-h-screen">
      <Hero />
      <Marquee />
      <StatsStrip />
      <Issues />
      <Answers />
      <Prototype />
      <Mockups />

      {/* ESTIMATE */}
      <Section id="estimate" className="!pt-0">
        <Reveal><Eyebrow>05 — ESTIMATE</Eyebrow></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-body font-medium tracking-[-0.03em] text-3xl sm:text-4xl lg:text-5xl mt-6 text-navy">
            Three fixed-price modules.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-mutedx text-lg mt-6 max-w-2xl leading-relaxed">
            One clean engagement: three fixed-price modules, everything included — and the current IMDB
            stays untouched until cutover. Hosting is separate.
          </p>
        </Reveal>
        <div className="mt-12"><EstimateTable /></div>
      </Section>

      <Footer />
    </main>
  );
}
